import { WizardView } from '@salecycle/ui-component-library';
import useNotifications from 'core/notifications/useNotifications';
import { AuthorisedRoute } from 'core/router/AuthorisedRoute';
import Spinner from 'core/ui/components/spinner';
import { Permission } from 'entities/user';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Switch, useRouteMatch } from 'react-router-dom';
import { useRolesContext } from '../../../context/RolesProvider';
import { useNewRoleDispatch, useNewRoleState } from '../context/useNewRoleContext';
import { createNewRole } from '../data/NewRoleApi';
import { getStep, getStepRoute, getSteps } from '../data/NewRoleConstants';
import { arePreviousStepsValid, getLastValidStepRoute } from '../utils/helpers';
import { NewRoleSetup } from './steps/NewRoleSetup';
import { NewRoleSummary } from './steps/NewRoleSummary';

const getPermissionsList = (permissions: { [key in Permission]: boolean }): Permission[] =>
  Object.keys(permissions)
    .filter(key => !!permissions[key])
    .map(key => key as Permission);

export const NewRoleWizard: React.FC = () => {
  const { t } = useTranslation();
  const steps = getSteps(t);
  const { push } = useHistory();
  const dispatch = useNewRoleDispatch();
  const { currentStep, name, description, permissions, users, validSteps } = useNewRoleState();
  const { addNewRole } = useRolesContext();
  const isCurrentStepValid = validSteps.includes(currentStep);
  const isSummaryStep = currentStep === steps.length;
  const { showNotification } = useNotifications();
  const returnToRolesTable = () => push('/user-management/roles');

  const routeMatcher = useRouteMatch<{ [key: string]: string }>({
    path: '/user-management/roles/create/:step',
  });

  const step = routeMatcher?.params?.step ?? '/';
  useEffect(() => {
    const stepNumber = getStep(step);

    if (!stepNumber) {
      return push(getStepRoute(1));
    }

    dispatch({ type: 'UPDATE_CURRENT_STEP', payload: { newStep: stepNumber } });
  }, [step, push, dispatch]);

  useEffect(() => {
    const prevStepsValid = arePreviousStepsValid(validSteps, currentStep);

    if (!prevStepsValid) {
      push(getLastValidStepRoute(validSteps));
    }
  }, [currentStep, validSteps, push]);

  const onNextButtonClicked = () => push(getStepRoute(currentStep + 1));
  const onPrevButtonClicked = () => push(getStepRoute(currentStep - 1));

  const createRole = async () => {
    try {
      const role = await createNewRole({
        name,
        description,
        permissions: getPermissionsList(permissions),
        users: users.map(option => option.value),
      });

      addNewRole(role);

      showNotification(
        t('newRole.notifications.success', {
          name,
        }),
      );
    } catch {
      showNotification(t('newRole.notifications.error'), 'error');
    } finally {
      returnToRolesTable();
    }
  };

  if (!currentStep) {
    return <Spinner />;
  }

  return (
    <WizardView
      currentStep={currentStep}
      steps={steps}
      exitButtonLabel={t('newRole.exitLabel')}
      onExitButtonClicked={returnToRolesTable}
      prevButtonLabel={t('newRole.backLabel')}
      prevButtonDisabled={currentStep === 1}
      onPrevButtonClicked={onPrevButtonClicked}
      nextButtonLabel={isSummaryStep ? t('newRole.completionLabel') : t('newRole.nextLabel')}
      nextButtonDisabled={!isCurrentStepValid && !isSummaryStep}
      onNextButtonClicked={isSummaryStep ? createRole : onNextButtonClicked}
    >
      <Switch>
        <AuthorisedRoute
          exact={true}
          path="/user-management/roles/create/role-setup"
          requiredAccessLevel="SaleCycle"
          requiredPermissions={['ManageUsers']}
          requiredFlags={['role-management-ui']}
          component={NewRoleSetup}
        />
        <AuthorisedRoute
          exact={true}
          path="/user-management/roles/create/summary"
          requiredAccessLevel="SaleCycle"
          requiredPermissions={['ManageUsers']}
          requiredFlags={['role-management-ui']}
          component={NewRoleSummary}
        />
      </Switch>
    </WizardView>
  );
};
